// src/components/ListasTrabajo/GestionListas/ListasTrabajoPanel.js

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    Tabs,
    Tab,
    TextField,
    InputAdornment,
    CircularProgress,
    Menu,
    MenuItem,
    Tooltip,
    IconButton,
    Chip,
} from '@mui/material';
import Alert from '@mui/material/Alert';

import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/SortByAlpha';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import FolderIcon from '@mui/icons-material/Folder';
import PeopleIcon from '@mui/icons-material/People';

import ListaCard from './ListaCard';
import CrearListaDialog from './CrearListaDialog';
import DetalleListaDialog from './DetalleListaDialog';
import ConfirmDialog from '../../Shared/ConfirmDialog';
import PromptDialog from '../../Shared/PromptDialog';
import useListasTrabajo from '../../../hooks/useListasTrabajo';
import CompartirListaDialog from './CompartirListaDialog';
import { listasTrabajoCompartidasJWT } from '../../../api/endpoints';

const ListasTrabajoPanel = ({ tipoExpediente = 1 }) => {
    const usuarioActual = window.SIGIF?.usuario || null;

    const {
        listas,
        loading,
        crearLista,
        actualizarLista,
        eliminarLista,
        duplicarLista,
        exportarLista,
        toggleFavorita,
        cargarListas
    } = useListasTrabajo(tipoExpediente, true);

    // Estado
    const [busqueda, setBusqueda] = useState('');
    const [tabActual, setTabActual] = useState(0);
    const [ordenarPor, setOrdenarPor] = useState('recientes');
    const [vistaGrid, setVistaGrid] = useState(true);

    // Compartidas
    const [listasCompartidas, setListasCompartidas] = useState([]);
    const [loadingCompartidas, setLoadingCompartidas] = useState(false);

    const cargarCompartidas = useCallback(async () => {
        setLoadingCompartidas(true);
        try {
            const resp = await listasTrabajoCompartidasJWT(tipoExpediente);
            if (resp?.ok) setListasCompartidas(resp.listas || []);
        } catch { /* ignore */ }
        setLoadingCompartidas(false);
    }, [tipoExpediente]);

    useEffect(() => {
        cargarCompartidas();
    }, [cargarCompartidas]);

    // Dialogs
    const [dialogCrear, setDialogCrear] = useState(false);
    const [dialogDetalle, setDialogDetalle] = useState(false);
    const [dialogConfirm, setDialogConfirm] = useState(false);
    const [dialogPrompt, setDialogPrompt] = useState(false);
    const [listaSeleccionada, setListaSeleccionada] = useState(null);
    const [promptConfig, setPromptConfig] = useState({});

    // Dialog compartir
    const [dialogCompartir, setDialogCompartir] = useState(false);

    // Menus
    const [menuOrden, setMenuOrden] = useState(null);

    // Filtrar y ordenar listas
    const listasFiltradas = useMemo(() => {
        // Tab 3 = Compartidas (fuente distinta)
        let resultado = tabActual === 3 ? [...listasCompartidas] : [...listas];

        if (busqueda.trim()) {
            const busquedaLower = busqueda.toLowerCase();
            resultado = resultado.filter(lista =>
                lista.Nombre.toLowerCase().includes(busquedaLower) ||
                lista.Descripcion?.toLowerCase().includes(busquedaLower)
            );
        }

        switch (tabActual) {
            case 1:
                resultado.sort((a, b) =>
                    new Date(b.Fecha_Modificacion) - new Date(a.Fecha_Modificacion)
                );
                resultado = resultado.slice(0, 10);
                break;
            case 2:
                resultado = resultado.filter(lista => lista.Favorita);
                break;
            case 3:
                // Compartidas - ya filtradas por fuente
                break;
            default:
                break;
        }

        switch (ordenarPor) {
            case 'nombre':
                resultado.sort((a, b) => a.Nombre.localeCompare(b.Nombre));
                break;
            case 'recientes':
                resultado.sort((a, b) =>
                    new Date(b.Fecha_Modificacion) - new Date(a.Fecha_Modificacion)
                );
                break;
            case 'cantidad':
                resultado.sort((a, b) =>
                    (b.Total_Expedientes || 0) - (a.Total_Expedientes || 0)
                );
                break;
            default:
                break;
        }

        return resultado;
    }, [listas, listasCompartidas, busqueda, tabActual, ordenarPor]);

    // Estadisticas
    const estadisticas = useMemo(() => {
        return {
            totalListas: listas.length,
            totalExpedientes: listas.reduce((sum, lista) => sum + (lista.Total_Expedientes || 0), 0),
            listasVacias: listas.filter(lista => (lista.Total_Expedientes || 0) === 0).length
        };
    }, [listas]);

    const [searchOpen, setSearchOpen] = useState(false);
    const searchRef = React.useRef(null);

    // Handlers
    const handleCrearLista = () => {
        setListaSeleccionada(null);
        setDialogCrear(true);
    };

    const handleEditarLista = (lista) => {
        setListaSeleccionada(lista);
        setDialogCrear(true);
    };

    const handleVerDetalle = (lista) => {
        setListaSeleccionada(lista);
        setDialogDetalle(true);
    };

    const handleEliminarLista = (lista) => {
        setListaSeleccionada(lista);
        setDialogConfirm(true);
    };

    const confirmarEliminarLista = async () => {
        if (listaSeleccionada) {
            await eliminarLista(listaSeleccionada.Id);
            await cargarListas();
            setListaSeleccionada(null);
        }
    };

    const handleDuplicarLista = (lista) => {
        setPromptConfig({
            title: 'Duplicar lista',
            label: 'Nombre de la nueva lista',
            defaultValue: `${lista.Nombre} (copia)`,
            onConfirm: async (nuevoNombre) => {
                await duplicarLista(lista.Id, nuevoNombre);
                await cargarListas();
            }
        });
        setListaSeleccionada(lista);
        setDialogPrompt(true);
    };

    const handleToggleFavorita = (lista) => toggleFavorita(lista.Id, lista.Favorita);

    const handleCompartir = (lista) => {
        setListaSeleccionada(lista);
        setDialogCompartir(true);
    };

    const handleExportarLista = async (idLista) => {
        await exportarLista(idLista);
    };

    const handleGuardarLista = async (datos) => {
        if (listaSeleccionada) {
            await actualizarLista(listaSeleccionada.Id, datos);
        } else {
            await crearLista(datos);
        }
        setDialogCrear(false);
        setListaSeleccionada(null);
        await cargarListas();
    };

    return (
        <Box sx={{ p: 2, backgroundColor: '#fafafa', minHeight: '100vh' }}>
            {/* Header compacto: titulo + stats + botón + controles en una sola sección */}
            <Box sx={{
                mb: 1.5,
                backgroundColor: 'white',
                p: 1.5,
                borderRadius: '4px',
                border: '1px solid #e6edf3',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
            }}>
                {/* Fila 1: Titulo + stats centradas + botón */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FolderIcon sx={{ fontSize: 24, color: '#2b3a47' }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#2b3a47' }}>
                            Listas de Trabajo
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6b6b6b' }}>
                            — {tipoExpediente === 1 ? 'Judiciales' : 'Administrativos'}
                        </Typography>
                    </Box>

                    <Box sx={{ flex: 1 }} />

                    {/* Stats centradas */}
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        <Box sx={{ p: 1, borderRadius: '4px', bgcolor: '#f8f9fa', border: '1px solid #e6edf3', textAlign: 'center', minWidth: 80 }}>
                            <Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1, color: '#1769aa' }}>{estadisticas.totalListas}</Typography>
                            <Typography sx={{ fontSize: 10, color: '#6b6b6b', fontWeight: 500, mt: 0.3 }}>Listas</Typography>
                        </Box>
                        <Box sx={{ p: 1, borderRadius: '4px', bgcolor: '#f8f9fa', border: '1px solid #e6edf3', textAlign: 'center', minWidth: 80 }}>
                            <Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1, color: '#2e7d32' }}>{estadisticas.totalExpedientes}</Typography>
                            <Typography sx={{ fontSize: 10, color: '#6b6b6b', fontWeight: 500, mt: 0.3 }}>Expedientes</Typography>
                        </Box>
                        <Box sx={{ p: 1, borderRadius: '4px', bgcolor: '#f8f9fa', border: '1px solid #e6edf3', textAlign: 'center', minWidth: 80 }}>
                            <Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1, color: estadisticas.listasVacias > 0 ? '#f44336' : '#9e9e9e' }}>{estadisticas.listasVacias}</Typography>
                            <Typography sx={{ fontSize: 10, color: '#6b6b6b', fontWeight: 500, mt: 0.3 }}>Vacías</Typography>
                        </Box>
                    </Box>

                    <Box sx={{ flex: 1 }} />

                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleCrearLista}
                        sx={{
                            backgroundColor: '#1769aa',
                            fontWeight: 600,
                            borderRadius: '4px',
                            textTransform: 'none',
                            boxShadow: '0 2px 8px rgba(22,103,183,0.25)',
                            px: 2,
                        }}
                    >
                        Nueva Lista
                    </Button>
                </Box>

                {/* Fila 2: Tabs + busqueda expandible + ordenar + vista */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tabs
                        value={tabActual}
                        onChange={(e, val) => setTabActual(val)}
                        indicatorColor="primary"
                        sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0, textTransform: 'none', fontSize: 13 } }}
                    >
                        <Tab label="Todas" />
                        <Tab label="Recientes" />
                        <Tab label="Favoritas" />
                        <Tab
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <PeopleIcon sx={{ fontSize: 16 }} />
                                    Compartidas
                                    {listasCompartidas.length > 0 && (
                                        <Chip label={listasCompartidas.length} size="small" color="primary"
                                            sx={{ height: 18, fontSize: 11, '& .MuiChip-label': { px: 0.5 } }} />
                                    )}
                                </Box>
                            }
                        />
                    </Tabs>

                    <Box sx={{ flex: 1 }} />

                    <Box display="flex" gap={0.5} alignItems="center">
                        {searchOpen ? (
                            <TextField
                                inputRef={searchRef}
                                size="small"
                                placeholder="Buscar listas..."
                                value={busqueda}
                                onChange={(e) => setBusqueda(e.target.value)}
                                onBlur={() => { if (!busqueda) setSearchOpen(false); }}
                                autoFocus
                                sx={{
                                    width: 200,
                                    transition: 'width 0.3s ease',
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: '4px',
                                        height: 32,
                                    }
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        ) : (
                            <Tooltip title="Buscar listas">
                                <IconButton
                                    size="small"
                                    onClick={() => setSearchOpen(true)}
                                    sx={{ border: '1px solid #e0e0e0', borderRadius: '4px', p: 0.5 }}
                                >
                                    <SearchIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}

                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<SortIcon />}
                                onClick={(e) => setMenuOrden(e.currentTarget)}
                                sx={{ textTransform: 'none', borderRadius: '4px', height: 32 }}
                            >
                                Ordenar
                            </Button>

                            <IconButton
                                size="small"
                                onClick={() => setVistaGrid(!vistaGrid)}
                                sx={{ border: '1px solid #e0e0e0', borderRadius: '4px', p: 0.5 }}
                            >
                                {vistaGrid ? <ViewListIcon fontSize="small" /> : <ViewModuleIcon fontSize="small" />}
                            </IconButton>
                        </Box>

                        <Menu
                            anchorEl={menuOrden}
                            open={Boolean(menuOrden)}
                            onClose={() => setMenuOrden(null)}
                        >
                            <MenuItem
                                onClick={() => { setOrdenarPor('recientes'); setMenuOrden(null); }}
                                selected={ordenarPor === 'recientes'}
                            >
                                Mas recientes
                            </MenuItem>
                            <MenuItem
                                onClick={() => { setOrdenarPor('nombre'); setMenuOrden(null); }}
                                selected={ordenarPor === 'nombre'}
                            >
                                Nombre (A-Z)
                            </MenuItem>
                            <MenuItem
                                onClick={() => { setOrdenarPor('cantidad'); setMenuOrden(null); }}
                                selected={ordenarPor === 'cantidad'}
                            >
                                Mas expedientes
                            </MenuItem>
                        </Menu>
                </Box>
            </Box>

            {/* Loading */}
            {(loading || (tabActual === 3 && loadingCompartidas)) && (
                <Box display="flex" justifyContent="center" py={8}>
                    <CircularProgress />
                </Box>
            )}

            {/* Sin listas */}
            {!loading && !loadingCompartidas && listasFiltradas.length === 0 && !busqueda && (
                <Alert severity="info" sx={{ mt: 1 }}>
                    {tabActual === 3
                        ? 'No hay listas compartidas contigo.'
                        : tabActual === 2
                            ? 'No tenés listas favoritas. Marcá una lista con la estrella.'
                            : 'No tenés listas creadas. Hacé clic en "Nueva Lista" para comenzar.'
                    }
                </Alert>
            )}

            {/* Sin resultados de búsqueda */}
            {!loading && !loadingCompartidas && listasFiltradas.length === 0 && busqueda && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                    No se encontraron listas que coincidan con "{busqueda}"
                </Alert>
            )}

            {/* Grid de Listas */}
            {!loading && listasFiltradas.length > 0 && (
                <Grid container spacing={2}>
                    {listasFiltradas.map((lista) => (
                        <Grid size={{ xs: 12, sm: vistaGrid ? 6 : 12, md: vistaGrid ? 4 : 12, lg: vistaGrid ? 3 : 12 }}
 key={lista.Id}
 >
                            <ListaCard
                                lista={lista}
                                onVerDetalle={() => handleVerDetalle(lista)}
                                onEditar={tabActual !== 3 ? () => handleEditarLista(lista) : undefined}
                                onEliminar={tabActual !== 3 ? () => handleEliminarLista(lista) : undefined}
                                onDuplicar={() => handleDuplicarLista(lista)}
                                onExportar={() => handleExportarLista(lista.Id)}
                                onToggleFavorita={() => handleToggleFavorita(lista)}
                                onCompartir={tabActual !== 3 ? () => handleCompartir(lista) : undefined}
                                vistaCompacta={vistaGrid}
                                compartida={tabActual === 3}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Dialogs */}
            <CrearListaDialog
                open={dialogCrear}
                onClose={() => {
                    setDialogCrear(false);
                    setListaSeleccionada(null);
                }}
                onGuardar={handleGuardarLista}
                lista={listaSeleccionada}
                tipoExpediente={tipoExpediente}
            />

            <DetalleListaDialog
                open={dialogDetalle}
                onClose={() => {
                    setDialogDetalle(false);
                    setListaSeleccionada(null);
                }}
                lista={listaSeleccionada}
                usuario={usuarioActual}
                onSuccess={() => {
                    cargarListas();
                }}
            />

            <ConfirmDialog
                open={dialogConfirm}
                onClose={() => {
                    setDialogConfirm(false);
                    setListaSeleccionada(null);
                }}
                onConfirm={confirmarEliminarLista}
                title="¿Eliminar lista?"
                message={`¿Esta seguro de eliminar la lista "${listaSeleccionada?.Nombre}"? Esta accion no se puede deshacer.`}
                confirmText="Eliminar"
                confirmColor="secondary"
            />

            <PromptDialog
                open={dialogPrompt}
                onClose={() => {
                    setDialogPrompt(false);
                    setListaSeleccionada(null);
                }}
                onConfirm={promptConfig.onConfirm}
                title={promptConfig.title}
                label={promptConfig.label}
                defaultValue={promptConfig.defaultValue}
            />

            <CompartirListaDialog
                open={dialogCompartir}
                onClose={() => {
                    setDialogCompartir(false);
                    setListaSeleccionada(null);
                }}
                lista={listaSeleccionada}
                onSuccess={() => cargarCompartidas()}
            />
        </Box>
    );
};

export default ListasTrabajoPanel;